package com.wintermar.inspection.feature.data.remote.mapper.template

import com.wintermar.inspection.GetFullTemplate
import com.wintermar.inspection.core.ui.util.SelectionModel
import com.wintermar.inspection.feature.domain.model.template.InspectionPageModel
import com.wintermar.inspection.feature.domain.model.template.InspectionSectionModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateQuestionModel
import com.wintermar.inspection.graphql.GetSimpleListTemplateQuery
import com.wintermar.inspection.graphql.InspectionTemplatesQuery
import com.wintermar.inspection.graphql.type.InspectionAnswerType
import com.wintermar.inspection.graphql.type.InspectionTemplateStatus
import migrations.TemplateTable

fun InspectionTemplatesQuery.InspectionTemplates.toListInspectionTemplateModel(): List<InspectionTemplateModel> {
    return data?.map { template ->
        InspectionTemplateModel(
            id = template.id.toString(),
            name = template.name.trim(),
            version = template.version,
            publishedDate = template.publishedDate?.toString() ?: "",
            numberOfPages = template.numberOfPages ?: 0,
            numberOfSections = template.numberOfSections ?: 0,
            numberOfQuestions = template.numberOfQuestions ?: 0,
            status = template.status,
            pages = template.pages.map { page ->
                InspectionPageModel(
                    id = page.id.toString(),
                    templateId = page.templateId.toString(),
                    order = page.order,
                    title = page.title.trim(),
                    sections = page.sections.map { section ->
                        InspectionSectionModel(
                            id = section.id.toString(),
                            pageId = section.pageId.toString(),
                            order = section.order,
                            title = section.title.trim(),
                            questions = section.questions.map { question ->
                                InspectionTemplateQuestionModel(
                                    id = question.id.toString(),
                                    sectionId = question.sectionId.toString(),
                                    questionText = question.questionText.trim(),
                                    guidelines = question.guidelines?.trim() ?: "",
                                    required = question.required,
                                    order = question.order,
                                    answerType = question.answerType,
                                    failedOptionId = question.failedOptionId?.toString() ?: "",
                                    neutralOptionId = question.neutralOptionId?.toString() ?: ""
                                )
                            }
                        )
                    }
                )
            }
        )
    } ?: emptyList()
}

fun List<GetFullTemplate>.toListInspectionTemplateModel(): InspectionTemplateModel {
    val template = this.first()
    val pages = this.groupBy { it.page_id }
        .filterKeys { it != null }
        .map { (pageId, pageRows) ->
            val firstPageRow = pageRows.first()

            val sections = pageRows.groupBy { it.section_id }
                .filterKeys { it != null }
                .map { (sectionId, sectionRows) ->
                    val firstSectionRow = sectionRows.first()

                    val questions = sectionRows
                        .filter { it.q_id != null }
                        .map { question ->
                            InspectionTemplateQuestionModel(
                                id = question.q_id ?: "",
                                sectionId = sectionId ?: "",
                                questionText = question.questionText ?: "",
                                guidelines = question.guidelines ?: "",
                                required = question.required ?: false,
                                order = (question.q_pos ?: 0).toInt(),
                                answerType = InspectionAnswerType.safeValueOf(
                                    question.answerType ?: InspectionAnswerType.YES_NO_NA.rawValue
                                ),
                                failedOptionId = question.failedOptionId ?: "",
                                neutralOptionId = question.neutralOptionId ?: ""
                            )
                        }

                    InspectionSectionModel(
                        id = sectionId ?: "",
                        pageId = pageId ?: "",
                        order = (firstSectionRow.section_pos ?: 0).toInt(),
                        title = firstSectionRow.section_title ?: "",
                        questions = questions
                    )
                }
            InspectionPageModel(
                id = pageId ?: "",
                templateId = template.template_id,
                order = (firstPageRow.page_pos ?: 0).toInt(),
                title = firstPageRow.page_title ?: "",
                sections = sections
            )
        }

    return InspectionTemplateModel(
        id = template.template_id,
        name = template.template_name,
        version = template.version?.toInt() ?: 0,
        publishedDate = template.publishedDate,
        numberOfPages = template.numberOfPages?.toInt() ?: 0,
        numberOfSections = template.numberOfSections?.toInt() ?: 0,
        numberOfQuestions = template.numberOfQuestions?.toInt() ?: 0,
        pages = pages
    )
}

fun List<TemplateTable>.toListInspectionTemplateModel(): List<InspectionTemplateModel> {
    return this.map {
        InspectionTemplateModel(
            id = it.id,
            name = it.name,
            version = it.version?.toInt() ?: 0,
            publishedDate = it.publishedDate,
            numberOfPages = it.numberOfPages?.toInt() ?: 0,
            numberOfSections = it.numberOfSections?.toInt() ?: 0,
            numberOfQuestions = it.numberOfQuestions?.toInt() ?: 0,
            status = InspectionTemplateStatus.safeValueOf(
                it.status.ifEmpty {
                    InspectionTemplateStatus.UNKNOWN__.rawValue
                }),
            pages = emptyList()
        )
    }
}

fun List<TemplateTable>.toListSelectionModel(): List<SelectionModel> {
    return this.map {
        SelectionModel(
            id = it.id,
            value = it.name
        )
    }
}

fun GetSimpleListTemplateQuery.InspectionTemplates.toListSelectionModel(): List<SelectionModel> {
    return data?.map {
        SelectionModel(
            id = it.id.toString(),
            value = it.name
        )
    } ?: emptyList()
}
